import React from 'react';
import { Button, CircularProgress } from '@mui/material';

/**
 * Submit button with built-in loading + disabled handling.
 *
 * Replaces the ~100 hand-rolled patterns of:
 *   <Button disabled={saving} startIcon={saving ? <CircularProgress size={18}/> : null}>
 *     {saving ? 'Saving…' : 'Save'}
 *   </Button>
 *
 * Usage:
 *   <SubmitButton loading={saving} onClick={handleSave}>
 *     Save
 *   </SubmitButton>
 *
 *   <SubmitButton loading={submitting} loadingLabel="Signing in…" type="submit">
 *     Sign in
 *   </SubmitButton>
 *
 * Props:
 *   loading        — boolean; shows spinner + disables the button
 *   loadingLabel   — text to show while loading (defaults to "<children>…")
 *   disabled       — extra disabled flag (combined with loading)
 *   variant        — passed through to MUI Button (default 'contained')
 *   color          — passed through (default 'primary')
 *   fullWidth      — passed through
 *   type, onClick, sx, ...rest — passed through
 */
const SubmitButton = ({
  loading = false,
  loadingLabel,
  disabled = false,
  variant = 'contained',
  color = 'primary',
  fullWidth = false,
  type = 'button',
  children,
  sx,
  ...rest
}) => {
  const computedLoadingLabel = loadingLabel
    ?? (typeof children === 'string' ? `${children}…` : 'Loading…');

  return (
    <Button
      type={type}
      variant={variant}
      color={color}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
      sx={sx}
      {...rest}
    >
      {loading ? computedLoadingLabel : children}
    </Button>
  );
};

export default SubmitButton;
